import { useEffect, useState, type KeyboardEvent } from 'react';
import { toast } from 'react-toastify';
import type {
  CampaignChannelCredentialStatus,
  ChatbotBotInstance,
  InstanceOutboundChannel,
  MessagingChannelKind,
  MessagingChannelPublic,
  TenantEmailAccountPublic,
  TenantFeature,
  WhatsAppChannel,
} from '@workspace/types';
import { chatbotAdminApi } from '@/features/chatbot/api/chatbotApi';
import { CHANNEL_CREDENTIAL_STATUS_LABEL } from '@workspace/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { hasModuleFeature } from '@/features/auth/moduleFeatures';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { messagingAdminApi } from '@/features/contact-agent/api/messagingApi';
import { contactAgentError } from '@/features/contact-agent/api/contactAgentScope';
import { CampaignTestSendPanel } from '@/features/contact-agent/components/CampaignTestSendPanel';

function statusOf(
  botChannel: MessagingChannelPublic | undefined,
  fallback: MessagingChannelPublic | undefined,
): CampaignChannelCredentialStatus {
  if (botChannel) {
    return 'configured';
  }
  if (fallback) {
    return 'inherited';
  }
  return 'missing';
}

function statusClass(status: CampaignChannelCredentialStatus): string {
  if (status === 'configured') {
    return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40';
  }
  if (status === 'inherited') {
    return 'bg-muted text-muted-foreground';
  }
  return 'bg-amber-50 text-amber-800 dark:bg-amber-950/40';
}

function preventFormSubmitOnEnter(event: KeyboardEvent<HTMLInputElement>): void {
  if (event.key === 'Enter') {
    event.preventDefault();
  }
}

export function InstanceCampaignChannelsCard({
  instanceId,
  tenantId,
  bots,
  whatsappChannels,
  enabledOutboundChannels,
  tenantFeatures,
}: {
  instanceId: string;
  tenantId?: string;
  bots: ChatbotBotInstance[];
  whatsappChannels: WhatsAppChannel[];
  enabledOutboundChannels: InstanceOutboundChannel[];
  tenantFeatures: TenantFeature[];
}) {
  const currentUser = useAppSelector(selectCurrentUser);
  const actorRole = currentUser?.role ?? 'USER';
  const [channels, setChannels] = useState<MessagingChannelPublic[]>([]);
  const [emailAccounts, setEmailAccounts] = useState<TenantEmailAccountPublic[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const listed = await messagingAdminApi.listChannels(instanceId, tenantId);
      setChannels(Array.isArray(listed) ? listed : []);
    } catch (error) {
      toast.error(contactAgentError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    void load();
    void chatbotAdminApi.listTenantEmailAccounts(tenantId).then(setEmailAccounts).catch(() => setEmailAccounts([]));
  }, [instanceId, tenantId]);

  const outbound = Array.isArray(enabledOutboundChannels) ? enabledOutboundChannels : [];
  const features = Array.isArray(tenantFeatures) ? tenantFeatures : [];
  const smsEnabled =
    outbound.includes('SMS') &&
    (hasModuleFeature(currentUser, 'sms-integration') || features.includes('sms-integration'));
  const emailEnabled =
    outbound.includes('EMAIL') &&
    (hasModuleFeature(currentUser, 'email-integration') || features.includes('email-integration'));

  const fallbackSms = channels.find(item => item.kind === 'SMS' && !item.instanceBotId);
  const fallbackEmail = channels.find(item => item.kind === 'EMAIL' && !item.instanceBotId);

  return (
    <div className='space-y-4 rounded-xl border p-4'>
      <div>
        <h3 className='text-lg font-medium'>Canales de campaña</h3>
        <p className='text-sm text-muted-foreground'>
          La instancia es el fallback. Si un bot tiene canal propio, se usa solo ese.
        </p>
      </div>
      {loading ? <p className='text-sm text-muted-foreground'>Cargando canales…</p> : null}

      <WhatsAppContactoStatus bots={bots} whatsappChannels={whatsappChannels} actorRole={actorRole} />

        <MessagingKindBlock
        kind='SMS'
        enabled={smsEnabled}
        disabledReason='Esta instancia no tiene SMS. Pedile al superadmin que lo habilite.'
        instanceId={instanceId}
        tenantId={tenantId}
        fallback={fallbackSms}
        bots={bots}
        channels={channels}
        actorRole={actorRole}
        onChanged={() => void load()}
      />
      <MessagingKindBlock
        kind='EMAIL'
        enabled={emailEnabled}
        disabledReason='Esta instancia no tiene email. Pedile al superadmin que lo habilite.'
        instanceId={instanceId}
        tenantId={tenantId}
        fallback={fallbackEmail}
        bots={bots}
        channels={channels}
        emailAccounts={emailAccounts}
        actorRole={actorRole}
        onChanged={() => void load()}
      />
    </div>
  );
}

function WhatsAppContactoStatus({
  bots,
  whatsappChannels,
  actorRole,
}: {
  bots: ChatbotBotInstance[];
  whatsappChannels: WhatsAppChannel[];
  actorRole: string;
}) {
  const contacto = whatsappChannels.filter(channel => channel.purpose === 'CONTACTO');
  return (
    <Card className='rounded-xl'>
      <CardHeader>
        <CardTitle className='text-base'>WhatsApp</CardTitle>
      </CardHeader>
      <CardContent className='space-y-3'>
        {bots.length === 0 ? (
          <p className='text-sm text-muted-foreground'>Todavía no hay bots en esta instancia.</p>
        ) : (
          bots.map(bot => {
            const channel = contacto.find(item => item.instanceBotId === bot.id);
            const status: CampaignChannelCredentialStatus = channel ? 'configured' : 'missing';
            return (
              <div key={bot.botId} className='space-y-3 rounded-lg border p-3'>
                <div className='flex items-center justify-between gap-2'>
                  <span className='text-sm font-medium'>{bot.name}</span>
                  <Badge className={statusClass(status)}>{CHANNEL_CREDENTIAL_STATUS_LABEL[status]}</Badge>
                </div>
                {channel ? (
                  <CampaignTestSendPanel
                    actorRole={actorRole}
                    channelKind='WHATSAPP'
                    whatsAppChannelId={channel.id}
                    tenantId={channel.tenantId}
                  />
                ) : (
                  <p className='text-xs text-muted-foreground'>
                    Configurá un canal de contacto en Administración → WhatsApp.
                  </p>
                )}
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

function MessagingKindBlock({
  kind,
  enabled,
  disabledReason,
  instanceId,
  tenantId,
  fallback,
  bots,
  channels,
  emailAccounts,
  actorRole,
  onChanged,
}: {
  kind: MessagingChannelKind;
  enabled: boolean;
  disabledReason: string;
  instanceId: string;
  tenantId?: string;
  fallback?: MessagingChannelPublic;
  bots: ChatbotBotInstance[];
  channels: MessagingChannelPublic[];
  emailAccounts?: TenantEmailAccountPublic[];
  actorRole: string;
  onChanged: () => void;
}) {
  const label = kind === 'SMS' ? 'SMS' : 'Avisos por correo';
  const readyEmailAccounts =
    kind === 'EMAIL' ? (emailAccounts ?? []).filter(account => account.ready) : [];
  if (!enabled) {
    return (
      <Card className='rounded-xl opacity-70'>
        <CardHeader>
          <CardTitle className='text-base'>{label}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-muted-foreground' title={disabledReason}>
            {disabledReason}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='rounded-xl'>
      <CardHeader>
        <CardTitle className='text-base'>{label} de la instancia</CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        {kind === 'EMAIL' && readyEmailAccounts.length === 0 ? (
          <p className='text-sm text-muted-foreground'>El correo todavía no está conectado.</p>
        ) : (
          <MessagingChannelForm
            kind={kind}
            instanceId={instanceId}
            tenantId={tenantId}
            instanceBotId={null}
            existing={fallback}
            readyEmailAccounts={readyEmailAccounts}
            onSaved={onChanged}
          />
        )}
        {fallback ? (
          <CampaignTestSendPanel
            actorRole={actorRole}
            channelKind={kind}
            messagingChannelId={fallback.id}
            tenantId={tenantId}
          />
        ) : null}
        {bots.map(bot => {
          if (!bot.id) {
            return null;
          }
          const botChannel = channels.find(
            item => item.kind === kind && item.instanceBotId === bot.id,
          );
          const status = statusOf(botChannel, fallback);
          return (
            <div key={`${kind}-${bot.botId}`} className='space-y-3 rounded-lg border p-3'>
              <div className='flex items-center justify-between gap-2'>
                <span className='text-sm font-medium'>{bot.name}</span>
                <Badge className={statusClass(status)}>{CHANNEL_CREDENTIAL_STATUS_LABEL[status]}</Badge>
              </div>
              {kind === 'EMAIL' && readyEmailAccounts.length === 0 ? (
                <p className='text-sm text-muted-foreground'>El correo todavía no está conectado.</p>
              ) : (
                <MessagingChannelForm
                  kind={kind}
                  instanceId={instanceId}
                  tenantId={tenantId}
                  instanceBotId={bot.id}
                  existing={botChannel}
                  readyEmailAccounts={readyEmailAccounts}
                  onSaved={onChanged}
                />
              )}
              {botChannel ? (
                <CampaignTestSendPanel
                  actorRole={actorRole}
                  channelKind={kind}
                  messagingChannelId={botChannel.id}
                  tenantId={tenantId}
                />
              ) : null}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function MessagingChannelForm({
  kind,
  instanceId,
  tenantId,
  instanceBotId,
  existing,
  readyEmailAccounts = [],
  onSaved,
}: {
  kind: MessagingChannelKind;
  instanceId: string;
  tenantId?: string;
  instanceBotId: string | null;
  existing?: MessagingChannelPublic;
  readyEmailAccounts?: TenantEmailAccountPublic[];
  onSaved: () => void;
}) {
  const [fromAddress, setFromAddress] = useState(existing?.fromAddress ?? '');
  const [accountSid, setAccountSid] = useState(existing?.accountSid ?? '');
  const [authToken, setAuthToken] = useState('');
  const [messagingServiceSid, setMessagingServiceSid] = useState(existing?.messagingServiceSid ?? '');
  const [emailAccountId, setEmailAccountId] = useState(existing?.emailAccountId ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFromAddress(existing?.fromAddress ?? '');
    setAccountSid(existing?.accountSid ?? '');
    setMessagingServiceSid(existing?.messagingServiceSid ?? '');
    setAuthToken('');
    setEmailAccountId(existing?.emailAccountId ?? '');
  }, [existing?.id, existing?.fromAddress, existing?.accountSid, existing?.messagingServiceSid, existing?.emailAccountId]);

  const resolvedEmail = (() => {
    if (kind !== 'EMAIL') {
      return null;
    }
    if (emailAccountId) {
      return readyEmailAccounts.find(account => account.id === emailAccountId) ?? null;
    }
    if (readyEmailAccounts.length === 1) {
      return readyEmailAccounts[0];
    }
    return null;
  })();

  const save = async () => {
    setSaving(true);
    try {
      if (existing) {
        await messagingAdminApi.updateChannel(
          existing.id,
          {
            fromAddress: kind === 'SMS' ? fromAddress : undefined,
            accountSid: kind === 'SMS' ? accountSid : undefined,
            authToken: kind === 'SMS' && authToken.trim() ? authToken : undefined,
            messagingServiceSid: kind === 'SMS' ? messagingServiceSid : undefined,
            emailAccountId:
              kind === 'EMAIL'
                ? emailAccountId || (readyEmailAccounts.length === 1 ? null : emailAccountId)
                : undefined,
          },
          tenantId,
        );
      } else {
        const picked =
          kind === 'EMAIL'
            ? resolvedEmail ??
              readyEmailAccounts.find(account => account.id === emailAccountId) ??
              null
            : null;
        if (kind === 'EMAIL' && !picked) {
          toast.error('Elegí un correo listo.');
          return;
        }
        await messagingAdminApi.createChannel(
          {
            instanceId,
            instanceBotId,
            kind,
            fromAddress: kind === 'EMAIL' ? picked!.fromAddress : fromAddress,
            accountSid: kind === 'SMS' ? accountSid : undefined,
            authToken: kind === 'SMS' ? authToken : undefined,
            messagingServiceSid: kind === 'SMS' ? messagingServiceSid || undefined : undefined,
            emailAccountId:
              kind === 'EMAIL'
                ? readyEmailAccounts.length === 1 && !emailAccountId
                  ? undefined
                  : picked!.id
                : undefined,
          },
          tenantId,
        );
      }
      toast.success(kind === 'SMS' ? 'SMS guardado' : 'Correo guardado');
      onSaved();
    } catch (error) {
      toast.error(contactAgentError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='space-y-3'>
      {kind === 'SMS' ? (
        <div className='space-y-2'>
          <Label>Número de origen</Label>
          <Input
            autoComplete='off'
            value={fromAddress}
            maxLength={255}
            onKeyDown={preventFormSubmitOnEnter}
            onChange={event => setFromAddress(event.target.value.slice(0, 255))}
          />
        </div>
      ) : null}
      {kind === 'SMS' ? (
        <>
          <div className='space-y-2'>
            <Label>Account SID</Label>
            <Input
              autoComplete='off'
              value={accountSid}
              maxLength={64}
              onKeyDown={preventFormSubmitOnEnter}
              onChange={event => setAccountSid(event.target.value.slice(0, 64))}
            />
          </div>
          <div className='space-y-2'>
            <Label>Token</Label>
            <Input
              type='password'
              autoComplete='new-password'
              value={authToken}
              maxLength={4096}
              onKeyDown={preventFormSubmitOnEnter}
              placeholder={existing?.hasAuthToken ? 'Token guardado. Escribí otro para rotarlo.' : ''}
              onChange={event => setAuthToken(event.target.value.slice(0, 4096))}
            />
          </div>
          <div className='space-y-2'>
            <Label>Messaging Service SID (opcional)</Label>
            <Input
              autoComplete='off'
              value={messagingServiceSid}
              maxLength={64}
              onKeyDown={preventFormSubmitOnEnter}
              onChange={event => setMessagingServiceSid(event.target.value.slice(0, 64))}
            />
          </div>
        </>
      ) : (
        <div className='space-y-2'>
          {resolvedEmail ? (
            <p className='text-sm text-muted-foreground'>Sale de {resolvedEmail.fromAddress}.</p>
          ) : readyEmailAccounts.length > 1 ? (
            <>
              <Label>Correo para avisos</Label>
              <select
                className='w-full border border-input bg-background text-foreground rounded-md p-2'
                value={emailAccountId}
                onChange={event => setEmailAccountId(event.target.value)}
              >
                <option value=''>Elegí una dirección</option>
                {readyEmailAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.fromAddress}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <p className='text-sm text-muted-foreground'>
              Sale de {readyEmailAccounts[0]?.fromAddress}.
            </p>
          )}
        </div>
      )}
      <Button type='button' variant='outline' disabled={saving} onClick={() => void save()}>
        {saving ? 'Guardando…' : existing ? 'Guardar canal' : 'Crear canal'}
      </Button>
    </div>
  );
}
